import React, { useContext } from "react";

import styles from "./EntriesList.module.css";
import { ButtonCategory } from "../ButtonCategory";
import EntryCard from "../EntryCard";
import Icon from "../Icon";
import AuthContext from "../../context/AuthContext";
import { LatLng } from "leaflet";
import UiContext from "../../context/UiContext";

type Props = {
  entries?: any[];
  userLikes?: string[];
  onLike: (postId: string) => void;
  onUnlike: (postId: string) => void;
  onGoBack: () => void;
};

const EntriesList: React.FC<Props> = ({
  entries,
  onLike,
  onUnlike,
  onGoBack,
}) => {
  const [isGettingLocation, setIsGettingLocation] = React.useState(false);
  const { state, setUserLocation } = useContext(AuthContext);
  const { state: UiState, closeModal } = useContext(UiContext);

  const [currentFilter, setCurrentFilter] = React.useState("popular");

  let sortedEntries = entries;

  // Request geolocation permission when button clicked
  const handleLocation = () => {
    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude } as LatLng);
        setCurrentFilter("nearest");
        setIsGettingLocation(false);
      },
      (error) => {
        console.log(error);
        setIsGettingLocation(false);
      }
    );
  };

  const onSetFilterToLocation = () => {
    if (!state.userLocation) {
      handleLocation();
    } else {
      setCurrentFilter("nearest");
    }
  };

  // Sort items by filter
  if (currentFilter === "popular") {
    sortedEntries = entries?.sort((a, b) => b.likes - a.likes);
  } else if (currentFilter === "newest") {
    sortedEntries = entries?.sort((a, b) => {
      const dateA = new Date(a._createdAt);
      const dateB = new Date(b._createdAt);

      return dateB.getTime() - dateA.getTime();
    });
  } else if (currentFilter === "nearest") {
    sortedEntries = entries?.sort((a, b) => {
      const userLocation = state.userLocation;

      if (!userLocation) {
        return 0;
      }

      // Calculate distance
      const distanceA = Math.sqrt(
        Math.pow(userLocation.lat - a?.location?.lat, 2) +
          Math.pow(userLocation.lng - a?.location?.lng, 2)
      );
      const distanceB = Math.sqrt(
        Math.pow(userLocation.lat - b?.location?.lat, 2) +
          Math.pow(userLocation.lng - b?.location?.lng, 2)
      );

      return distanceA - distanceB;
    });
  }

  const handleLocate = (location: [number, number]) => {
    if (UiState.mapElement) {
      UiState.mapElement.flyTo(location, 16);
      closeModal();
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <button className={styles.button} onClick={onGoBack}>
          <Icon type="arrow" size="small" mirrored />
        </button>

        <div className={styles["header-inner"]}>
          <h3 className={styles.title}>Utforsk innspel</h3>
        </div>

        <div className={styles.button} style={{ opacity: 0 }}></div>
      </div>

      <div className={styles.filters}>
        <ButtonCategory
          icon="star"
          text="Mest populære"
          onClick={() => setCurrentFilter("popular")}
          active={currentFilter === "popular"}
        />
        <ButtonCategory
          icon="newest"
          text="Nyaste innspel"
          onClick={() => setCurrentFilter("newest")}
          active={currentFilter === "newest"}
        />
        <ButtonCategory
          icon="nearest"
          text="Nærmast deg"
          onClick={() => onSetFilterToLocation()}
          active={currentFilter === "nearest"}
          loading={isGettingLocation}
        />
      </div>

      <ul className={styles.list}>
        {sortedEntries?.map((entry) => (
          <li key={entry._id} className={styles.item}>
            <EntryCard
              title={entry?.title}
              description={entry?.description}
              userName={entry?.user?.name}
              category={entry?.category?.title}
              likes={entry?.likes}
              hasLiked={state.userLikes?.includes(entry._id)}
              onLike={() => onLike(entry._id)}
              onUnlike={() => onUnlike(entry._id)}
              onLocate={() =>
                handleLocate([entry?.location?.lat, entry?.location?.lng])
              }
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default EntriesList;
